import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { ArrowLeft, Mail, Instagram, Youtube, Music, Edit, Save, X } from 'lucide-react';
interface CreatorProfile {
  id: string;
  name: string;
  handle: string;
  avatar: string;
  email: string;
  followers: number;
  engagement: number;
  growth: number;
  status: 'active' | 'growing' | 'stable' | 'inactive';
  platforms: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
  };
  niches: string[];
  notes: string;
  joinedDate: string;
}
const mockCreators: CreatorProfile[] = [{
  id: '1',
  name: 'Sarah Klein',
  handle: 'sarah.style',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800',
  email: 'sarah@example.com',
  followers: 245000,
  engagement: 3.2,
  growth: 2.4,
  status: 'active',
  platforms: {
    instagram: 'https://instagram.com/sarah.style',
    tiktok: 'https://tiktok.com/@sarah.style',
    youtube: 'https://youtube.com/@sarahstyle'
  },
  niches: ['Fashion', 'Lifestyle', 'Beauty'],
  notes: 'Excellent engagement with fashion brands. Prefers long-term partnerships.',
  joinedDate: '2023-01-15'
}, {
  id: '2',
  name: 'Tom Weber',
  handle: 'tom_travels',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=800',
  email: 'tom@example.com',
  followers: 89000,
  engagement: 5.8,
  growth: 12.5,
  status: 'growing',
  platforms: {
    instagram: 'https://instagram.com/tom_travels',
    youtube: 'https://youtube.com/@tomtravels'
  },
  niches: ['Travel', 'Adventure', 'Photography'],
  notes: 'High engagement rate. Great for adventure and outdoor brands.',
  joinedDate: '2023-03-20'
}];
export function CreatorDetailPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [isEditing, setIsEditing] = useState(false);
  const [creator, setCreator] = useState(mockCreators.find(c => c.id === id));
  if (!creator) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-787">
        Creator not found
      </div>;
  }
  const statusVariant = creator.status === 'active' ? 'success' : creator.status === 'growing' ? 'default' : 'secondary';
  return <div className="min-h-screen bg-gray-50 pb-12" data-id="element-788">
      <header className="bg-white border-b sticky top-0 z-10" data-id="element-789">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-790">
          <div className="flex items-center" data-id="element-791">
            <Button variant="ghost" size="sm" className="mr-4" onClick={() => navigate('/manager')} data-id="element-792">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-793" /> Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900" data-id="element-794">{creator.name}</h1>
          </div>
          <div className="flex items-center space-x-2" data-id="element-795">
            {isEditing ? <>
                <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} data-id="element-796">
                  <X className="h-4 w-4 mr-2" data-id="element-797" /> Cancel
                </Button>
                <Button variant="primary" size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setIsEditing(false)} data-id="element-798">
                  <Save className="h-4 w-4 mr-2" data-id="element-799" /> Save Changes
                </Button>
              </> : <Button variant="secondary" size="sm" onClick={() => setIsEditing(true)} data-id="element-800">
                <Edit className="h-4 w-4 mr-2" data-id="element-801" /> Edit Profile
              </Button>}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-802">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-803">
          {/* Left Column - Profile Info */}
          <div className="lg:col-span-1 space-y-6" data-id="element-804">
            <Card data-id="element-805">
              <CardContent className="p-6 text-center" data-id="element-806">
                <div className="h-24 w-24 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 p-[3px] mx-auto mb-4" data-id="element-807">
                  <img src={creator.avatar} alt={creator.name} className="h-full w-full rounded-full object-cover border-4 border-white" data-id="element-808" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-809">
                  {creator.name}
                </h2>
                <p className="text-gray-500 mb-3" data-id="element-810">@{creator.handle}</p>
                <Badge variant={statusVariant} className="capitalize mb-4" data-id="element-811">
                  {creator.status}
                </Badge>

                <div className="space-y-3 text-left mt-6" data-id="element-812">
                  <div className="flex items-center text-sm" data-id="element-813">
                    <Mail className="h-4 w-4 text-gray-400 mr-3" data-id="element-814" />
                    {isEditing ? <Input type="email" value={creator.email} className="text-sm" data-id="element-815" /> : <a href={`mailto:${creator.email}`} className="text-blue-600 hover:underline" data-id="element-816">
                        {creator.email}
                      </a>}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-id="element-817">
              <CardHeader data-id="element-818">
                <CardTitle className="text-base" data-id="element-819">Platform Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-id="element-820">
                {creator.platforms.instagram && <div className="flex items-center space-x-3" data-id="element-821">
                    <div className="h-8 w-8 bg-pink-100 rounded-full flex items-center justify-center" data-id="element-822">
                      <Instagram className="h-4 w-4 text-pink-600" data-id="element-823" />
                    </div>
                    {isEditing ? <Input value={creator.platforms.instagram} className="text-sm" data-id="element-824" /> : <a href={creator.platforms.instagram} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex-1 truncate" data-id="element-825">
                        {creator.platforms.instagram}
                      </a>}
                  </div>}
                {creator.platforms.tiktok && <div className="flex items-center space-x-3" data-id="element-826">
                    <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-827">
                      <Music className="h-4 w-4 text-gray-900" data-id="element-828" />
                    </div>
                    {isEditing ? <Input value={creator.platforms.tiktok} className="text-sm" data-id="element-829" /> : <a href={creator.platforms.tiktok} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex-1 truncate" data-id="element-830">
                        {creator.platforms.tiktok}
                      </a>}
                  </div>}
                {creator.platforms.youtube && <div className="flex items-center space-x-3" data-id="element-831">
                    <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center" data-id="element-832">
                      <Youtube className="h-4 w-4 text-red-600" data-id="element-833" />
                    </div>
                    {isEditing ? <Input value={creator.platforms.youtube} className="text-sm" data-id="element-834" /> : <a href={creator.platforms.youtube} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex-1 truncate" data-id="element-835">
                        {creator.platforms.youtube}
                      </a>}
                  </div>}
              </CardContent>
            </Card>

            <Card data-id="element-836">
              <CardHeader data-id="element-837">
                <CardTitle className="text-base" data-id="element-838">Niches</CardTitle>
              </CardHeader>
              <CardContent data-id="element-839">
                <div className="flex flex-wrap gap-2" data-id="element-840">
                  {creator.niches.map((niche, index) => <Badge key={index} variant="secondary" data-id="element-841">
                      {niche}
                    </Badge>)}
                  {isEditing && <Button variant="ghost" size="sm" className="h-6 text-xs" data-id="element-842">
                      + Add Niche
                    </Button>}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Stats & Notes */}
          <div className="lg:col-span-2 space-y-6" data-id="element-843">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-844">
              <Card data-id="element-845">
                <CardContent className="p-6" data-id="element-846">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-847">Followers</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-848">
                    {(creator.followers / 1000).toFixed(1)}k
                  </p>
                  <p className="text-xs text-green-600 mt-1" data-id="element-849">
                    +{creator.growth}% growth
                  </p>
                </CardContent>
              </Card>
              <Card data-id="element-850">
                <CardContent className="p-6" data-id="element-851">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-852">Engagement Rate</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-853">
                    {creator.engagement}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-854">Above average</p>
                </CardContent>
              </Card>
              <Card data-id="element-855">
                <CardContent className="p-6" data-id="element-856">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-857">Member Since</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-858">
                    {new Date(creator.joinedDate).toLocaleDateString('en-US', {
                    month: 'short',
                    year: 'numeric'
                  })}
                  </p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-859">
                    {Math.floor((Date.now() - new Date(creator.joinedDate).getTime()) / (1000 * 3600 * 24 * 30))}{' '}
                    months
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card data-id="element-860">
              <CardHeader data-id="element-861">
                <CardTitle data-id="element-862">Internal Notes</CardTitle>
              </CardHeader>
              <CardContent data-id="element-863">
                {isEditing ? <textarea value={creator.notes} onChange={e => setCreator({
                ...creator,
                notes: e.target.value
              })} className="w-full h-32 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Add internal notes about this creator..." data-id="element-864" /> : <p className="text-gray-700 whitespace-pre-wrap" data-id="element-865">
                    {creator.notes}
                  </p>}
              </CardContent>
            </Card>

            <Card data-id="element-866">
              <CardHeader data-id="element-867">
                <CardTitle data-id="element-868">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent data-id="element-869">
                <div className="space-y-4" data-id="element-870">
                  <div className="flex items-start space-x-3 pb-4 border-b" data-id="element-871">
                    <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-872">
                      <span className="text-xs font-bold text-green-600" data-id="element-873">
                        ✓
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-874">
                      <p className="text-sm font-medium text-gray-900" data-id="element-875">
                        Completed Nike Summer Campaign
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-876">2 days ago</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 pb-4 border-b" data-id="element-877">
                    <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-878">
                      <span className="text-xs font-bold text-blue-600" data-id="element-879">↑</span>
                    </div>
                    <div className="flex-1" data-id="element-880">
                      <p className="text-sm font-medium text-gray-900" data-id="element-881">
                        Submitted deliverables for review
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-882">5 days ago</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-883">
                    <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-884">
                      <span className="text-xs font-bold text-purple-600" data-id="element-885">
                        ★
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-886">
                      <p className="text-sm font-medium text-gray-900" data-id="element-887">
                        Started new Adidas collaboration
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-888">1 week ago</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>;
}